import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { SMSIcon } from '@rsos/assets/icons';
import { Text } from '@rsos/base-components';
import {
  SOURCE_ADMIN_SMS,
  SOURCE_TCAPI,
} from '@rsos/constants/locationSources';
import {
  RCS_ACCEPT_STATUS_NONE,
  RCS_ACCEPT_STATUS_TIMED_OUT,
} from '@rsos/constants/rcsStatuses';
import useHasAccessToAirAmbulance from '@rsos/hooks/useHasAccessToAirAmbulance';
import { useGetConversation } from '@rsos/reduxDataStore/src/features/conversations/utils';
import { StateWithEmergencyCalls } from '@rsos/reduxDataStore/src/features/emergencyCalls/emergencyCallsSlice';
import { useSelectedRcsConversation } from '@rsos/reduxDataStore/src/features/rcs/rcsSelectors';
import { getMostRecentLocations } from '@rsos/utils/getMostRecentLoc';
import type { RootState } from '../../../../../app';
import EENCamerasBanner from '../../../../AdditionalData/EENCamerasBanner';
import StatusBanner from '../../../../AirAmbulanceForm/Banner/StatusBanner';
import { selectLatestDataset } from '../../../../Map/useDeckGL/EagleEye/helpers';
import TextFrom911BannersContainer from '../../../../TextFrom911/TextFrom911BannersContainer';
import CollaboratorBanner from '../CollaboratorBanner';
import InfoBanner from './InfoBanner';
import { RcsNotification, TextTo911Notification } from './OverviewTab.styles';

// TO DO: Remove when there is a Typing for Eagle Eye Slice
interface LatestDataset {
  topic?: number;
  dataset?: unknown;
}

/**
 * Single Component to maintain all banners that appear above the location card.
 */
const OverviewBanners = () => {
  const hasAccessToAirAmbulance = useHasAccessToAirAmbulance();

  const selectedCallerID = useSelector(
    (state: StateWithEmergencyCalls) => state.emergencyCalls.selectedCallerID,
  );

  if (!selectedCallerID) {
    throw 'Overview Banners rendered outside of Selected Call Pane';
  }

  const selectedCall = useSelector(
    (state: StateWithEmergencyCalls) =>
      state.emergencyCalls.callQueue[selectedCallerID],
  );
  const hasAirAmbulanceStatus = Boolean(
    useSelector(
      (state: RootState) =>
        // TODO: whoever knows the correct type here should add it by porting airAmbulanceSlice to TypeScript (preferred) or at the very least adding a declaration file for it.
        (state.airAmbulance.requests.calls as any)[selectedCallerID],
    ),
  );

  const eenLatestDataset = useSelector(selectLatestDataset) as
    | LatestDataset
    | undefined;

  const isCamerasAvailable =
    selectedCallerID &&
    eenLatestDataset &&
    parseInt(selectedCallerID) === eenLatestDataset.topic;

  const mostRecentLocation = useMemo(() => {
    const mostRecentLocations = getMostRecentLocations(selectedCall);
    return (
      mostRecentLocations.mostRecentLocation ||
      mostRecentLocations.mostRecentSecondaryLocation || { source: '' }
    );
  }, [selectedCall]);

  const rcsConversation = useSelectedRcsConversation(selectedCallerID);

  const isRcsTimedOut = useMemo(() => {
    return (
      rcsConversation &&
      rcsConversation.data &&
      rcsConversation.data.acceptStatus === RCS_ACCEPT_STATUS_TIMED_OUT
    );
  }, [rcsConversation]);

  const shouldDisplayRcsNotification = useMemo(() => {
    return (
      rcsConversation &&
      rcsConversation.data &&
      (rcsConversation.data.acceptStatus === RCS_ACCEPT_STATUS_NONE ||
        rcsConversation.data.acceptStatus === RCS_ACCEPT_STATUS_TIMED_OUT)
    );
  }, [rcsConversation]);

  const tcapiConversation = useGetConversation(selectedCallerID);

  const isTcapiTimedOut = useMemo(() => {
    if (
      !tcapiConversation ||
      tcapiConversation.source !== SOURCE_TCAPI ||
      !tcapiConversation.data
    ) {
      return false;
    }
    return tcapiConversation.data.acceptStatus === 'timed_out';
  }, [tcapiConversation]);

  const shouldDisplayTcapiNotification = useMemo(() => {
    if (
      !tcapiConversation ||
      tcapiConversation.source !== SOURCE_TCAPI ||
      !tcapiConversation.data
    ) {
      return false;
    }
    return (
      tcapiConversation.data.acceptStatus === 'pending' ||
      tcapiConversation.data.acceptStatus === 'timed_out'
    );
  }, [tcapiConversation]);

  return (
    <>
      {shouldDisplayRcsNotification && (
        <RcsNotification
          backgroundColor={isRcsTimedOut ? 'lightDanger' : 'success'}
        >
          <SMSIcon />
          <Text size="normal">
            {!isRcsTimedOut && <>Google RCS messaging available</>}
            {isRcsTimedOut && <>RCS Messaging is no longer available</>}
          </Text>
        </RcsNotification>
      )}
      {selectedCall?.isAdmin && (
        <InfoBanner>
          Admin line call: location and additional data unavailable.
        </InfoBanner>
      )}
      <CollaboratorBanner />
      {shouldDisplayTcapiNotification && (
        <TextTo911Notification
          backgroundColor={isTcapiTimedOut ? 'lightDanger' : 'success'}
        >
          <SMSIcon />
          <Text size="normal">
            {!isTcapiTimedOut && <>Text-to-911: Inbound Emergency Request</>}
            {isTcapiTimedOut && (
              <>Text-to-911 conversation is no longer available</>
            )}
          </Text>
        </TextTo911Notification>
      )}
      {mostRecentLocation?.source === SOURCE_ADMIN_SMS && (
        <InfoBanner>
          Admin line text: location and additional data unavailable.
        </InfoBanner>
      )}
      {hasAirAmbulanceStatus && hasAccessToAirAmbulance && (
        <StatusBanner showChevron />
      )}
      <TextFrom911BannersContainer />
      {isCamerasAvailable && <EENCamerasBanner />}
    </>
  );
};

export default OverviewBanners;
